import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './News.css';
import { fetchDigest } from './newsApi';
import { SECTION_LABELS, formatDDay, formatDayLabel, groupItemsBySection } from './newsFormat';
import NewsSourceLink from './components/NewsSourceLink';
import NewsQuiz from './components/NewsQuiz';
import NewsClassroomIdea from './components/NewsClassroomIdea';
import NewsWhatquizNews from './components/NewsWhatquizNews';
import NewsMoreItems from './components/NewsMoreItems';
import NewsCardDeck from './components/NewsCardDeck';
import NewsStudentBrief from './components/NewsStudentBrief';
import NewsTechPick from './components/NewsTechPick';
import NewsSubscribe from './components/NewsSubscribe';

const SITE = 'https://whatquiz.co.kr';

const NewsSkeleton = () => (
  <div className="news-page" aria-busy="true" aria-label="뉴스를 불러오는 중">
    <div className="news-skeleton news-skeleton--eyebrow" />
    <div className="news-skeleton news-skeleton--title" />
    <div className="news-skeleton news-skeleton--card" />
    <div className="news-skeleton news-skeleton--card" />
  </div>
);

const NewsEmpty = ({ title, description }) => (
  <div className="news-page news-empty">
    <Helmet><meta name="robots" content="noindex" /></Helmet>
    <h1>{title}</h1>
    <p>{description}</p>
    <Link to="/news" className="news-btn-primary">지난 뉴스 보기</Link>
  </div>
);

const NewsDetailPage = () => {
  const { day } = useParams();
  const { hash } = useLocation();
  const [state, setState] = useState({ status: 'loading', digest: null });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', digest: null });
    fetchDigest(day)
      .then((digest) => !cancelled && setState({ status: digest ? 'ready' : 'missing', digest }))
      .catch(() => !cancelled && setState({ status: 'error', digest: null }));
    return () => { cancelled = true; };
  }, [day]);

  // 메일의 "정답 확인하기"(#quiz)·"학습지 만들기"(#idea) 링크는 내용을 불러온 뒤에야 위치가 생긴다.
  useEffect(() => {
    if (state.status !== 'ready' || !hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [state.status, hash]);

  if (state.status === 'loading') return <NewsSkeleton />;
  if (state.status === 'missing') {
    return <NewsEmpty title="이 날짜의 뉴스가 없어요" description="아침 8시에 새 소식이 올라와요." />;
  }
  if (state.status === 'error') {
    return <NewsEmpty title="뉴스를 불러오지 못했어요" description="잠시 후 다시 들어와 주세요." />;
  }

  const digest = state.digest;
  const sources = digest.sources || {};
  const top = digest.top_story;
  const dayLabel = formatDayLabel(digest.date);
  const description = (digest.three_lines || []).map((line) => line.text).join(' ');
  const canonical = `${SITE}/news/${digest.date}`;
  const bodyIds = new Set([top?.item_id, ...(digest.items || []).map((item) => item.item_id)].filter(Boolean));
  const bodyHref = (itemId) => (bodyIds.has(itemId) ? `#item-${itemId}` : null);
  const ogImage = (digest.cards || []).find((card) => card.image_url)?.image_url || `${SITE}/og-image-whatquiz-2025.png`;

  return (
    <div className="news-page">
      <Helmet>
        <title>{`${digest.headline} | ${dayLabel} 아침 교육 뉴스 | 왓퀴즈`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={`${digest.headline} | 아침 교육 뉴스`} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'NewsArticle',
            headline: digest.headline,
            description,
            datePublished: digest.published_at || `${digest.date}T08:00:00+09:00`,
            inLanguage: 'ko-KR',
            isAccessibleForFree: true,
            mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
            author: { '@type': 'Organization', name: '왓퀴즈', url: SITE },
            publisher: {
              '@type': 'Organization',
              name: '왓퀴즈',
              url: SITE,
              logo: { '@type': 'ImageObject', url: `${SITE}/favicon.png` },
            },
            image: ogImage,
          })}
        </script>
      </Helmet>

      <header className="news-hero">
        <p className="news-eyebrow">{dayLabel} 아침 교육 뉴스</p>
        <h1 className="news-headline">{digest.headline}</h1>
      </header>

      <NewsCardDeck
        cards={digest.cards}
        linkFor={(card) => (card.kind === 'quiz' && digest.quiz ? '#quiz' : bodyHref(card.item_id))}
      />

      {digest.three_lines?.length > 0 && (
        <section className="news-card news-three" aria-label="오늘의 3줄 요약">
          <p className="news-eyebrow">바쁜 선생님을 위한 요약</p>
          <ol className="news-three-list">
            {digest.three_lines.map((line) => {
              const href = bodyHref(line.item_id);
              return (
                <li key={line.item_id || line.text}>
                  {href ? <a className="news-three-link" href={href}>{line.text}<span aria-hidden="true"> ›</span></a> : line.text}
                </li>
              );
            })}
          </ol>
        </section>
      )}

      {top && (
        <article id={`item-${top.item_id}`} className="news-top" aria-labelledby="news-top-title">
          <p className="news-eyebrow">오늘의 주요 뉴스</p>
          <h2 id="news-top-title" className="news-top-title">{top.title}</h2>
          {top.numbers?.length > 0 && (
            <div className="news-numbers">
              {top.numbers.map((number) => (
                <div key={`${number.value}-${number.label}`} className="news-number">
                  <strong>{number.value}</strong>
                  <span>{number.label}</span>
                </div>
              ))}
            </div>
          )}
          <p className="news-body">{top.what}</p>
          {top.why && (
            <div className="news-why">
              <span>선생님이 챙길 점</span>
              <p>{top.why}</p>
            </div>
          )}
          {top.next && <p className="news-next">다음 일정 · {top.next}</p>}
          <NewsSourceLink source={sources[top.item_id]} />
        </article>
      )}

      {digest.calendar?.length > 0 && (
        <section className="news-section" aria-labelledby="news-calendar-title">
          <h2 id="news-calendar-title" className="news-section-title">다가오는 일정</h2>
          <div className="news-dday-list">
            {digest.calendar.map((event) => (
              <a
                key={`${event.date}-${event.title}`}
                className="news-dday"
                href={event.source}
                target="_blank"
                rel="noopener noreferrer"
              >
                <strong>{formatDDay(event.d_day)}</strong>
                <span>{event.title}</span>
                <small>{formatDayLabel(event.date)}</small>
              </a>
            ))}
          </div>
        </section>
      )}

      {groupItemsBySection(digest.items).map((group) => (
        <section key={group.section} className="news-section" aria-labelledby={`news-sec-${group.section}`}>
          <h2 id={`news-sec-${group.section}`} className="news-section-title">{SECTION_LABELS[group.section]}</h2>
          <div className="news-item-list">
            {group.items.map((item) => (
              <article key={item.item_id} id={`item-${item.item_id}`} className="news-card news-item">
                <h3 className="news-item-title">{item.title}</h3>
                <p className="news-body">{item.summary}</p>
                {item.why && <p className="news-item-why">{item.why}</p>}
                <NewsSourceLink source={sources[item.item_id]} />
              </article>
            ))}
          </div>
        </section>
      ))}

      <NewsTechPick pick={digest.tech_pick} source={sources[digest.tech_pick?.item_id]} />
      <NewsMoreItems items={digest.more_items} />
      <NewsStudentBrief brief={digest.student_brief} source={sources[digest.student_brief?.item_id]} />
      <NewsClassroomIdea idea={digest.classroom_idea} date={digest.date} />
      <NewsQuiz quiz={digest.quiz} source={sources[digest.quiz?.item_id]} date={digest.date} />
      <NewsWhatquizNews items={digest.whatquiz_news} />
      <NewsSubscribe />

      <footer className="news-footer">
        <p className="news-ai-notice">{digest.ai_notice}</p>
        <Link to="/news" className="news-btn-secondary">지난 뉴스 보기</Link>
      </footer>
    </div>
  );
};

export default NewsDetailPage;
